import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList, Loader2 } from 'lucide-react';
import { useAuth } from '../../../../hooks';
import { getStudentAssignment, startStudentAssignment } from '../../../../api';
import { withAuth } from '../../../auth/withAuth';
import LoggedInUserWrapper from '../../../core/LoggedInUserWrapper';
import { getAssignmentQuestionUrl, getAssignmentEndUrl, shouldRevealSolutions } from '../../../../utils';
import { STUDENTASSIGNMENT_STATUS } from '../../../../constants';
import { Button } from '../../../ui/button';
import { Card } from '../../../ui/card';

const FALLBACK_LOAD_ERROR = "We couldn't load this assignment. Check the link and try again.";

const reasonFromError = (error) => {
  const data = error?.response?.data;
  const message = data?.message || data?.error;
  if (typeof message === 'string' && message.trim()) return message.trim();
  if (Array.isArray(message) && message[0]) return String(message[0]);
  const axiosMessage = error?.message;
  if (axiosMessage && !/^Request failed with status code \d+$/i.test(axiosMessage)) {
    return axiosMessage;
  }
  return FALLBACK_LOAD_ERROR;
};

const unwrapAssignment = (payload) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.studentResponse || payload.status) return payload;
  if (payload.data && typeof payload.data === 'object') return unwrapAssignment(payload.data);
  return payload;
};

const firstQuestionId = (assignment) => assignment?.studentResponse?.[0]?.questionId;

const errorTitle = (errorMessage) => {
  if (/not found|invalid student assignment/i.test(errorMessage)) return 'Assignment not found';
  return "Couldn't load assignment";
};

const AssignmentHome = () => {
  const { token } = useAuth();
  const { studentAssignmentId } = useParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const openAssignment = async (assignment) => {
      const questionId = firstQuestionId(assignment);
      if (questionId) {
        navigate(getAssignmentQuestionUrl({ studentAssignmentId, questionId }));
        return true;
      }
      return false;
    };

    const fetchAssignmentAndRedirect = async () => {
      setErrorMessage('');

      try {
        const assignment = unwrapAssignment(
          await getStudentAssignment({ studentAssignmentId, token })
        );
        if (cancelled) return;

        if (assignment?.status === STUDENTASSIGNMENT_STATUS.COMPLETED) {
          navigate(
            shouldRevealSolutions(assignment)
              ? `/assignment/${studentAssignmentId}/review`
              : getAssignmentEndUrl({ studentAssignmentId })
          );
          return;
        }

        if (await openAssignment(assignment)) return;
        if (cancelled) return;

        if (assignment?.status === STUDENTASSIGNMENT_STATUS.YET_TO_START) {
          const started = unwrapAssignment(
            (await startStudentAssignment({ studentAssignmentId, token }))?.data
          );
          if (cancelled) return;
          if (await openAssignment(started)) return;
        }

        if (cancelled) return;

        setErrorMessage(
          assignment?.status === STUDENTASSIGNMENT_STATUS.YET_TO_START
            ? "This assignment hasn't been started yet, so there are no questions to open."
            : 'This assignment has no questions.'
        );
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(reasonFromError(error));
        }
      }
    };

    fetchAssignmentAndRedirect();
    return () => {
      cancelled = true;
    };
  }, [navigate, studentAssignmentId, token, retryCount]);

  const goToActivity = () => navigate('/activity');

  return (
    <LoggedInUserWrapper welcomeMode>
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-12">
        <div className="w-full max-w-md animate-in fade-in slide-in-from-bottom-4 duration-500">
          {errorMessage ? (
            <div className="text-center">
              <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-rose-50">
                <ClipboardList className="h-8 w-8 text-rose-500" aria-hidden />
              </div>
              <h1 className="text-xl font-semibold text-foreground md:text-2xl">
                {errorTitle(errorMessage)}
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{errorMessage}</p>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                <Button type="button" variant="outline" onClick={goToActivity} className="min-w-[160px]">
                  <ClipboardList className="h-4 w-4" />
                  Go to Activity
                </Button>
                <Button
                  type="button"
                  onClick={() => setRetryCount((count) => count + 1)}
                  className="min-w-[160px]"
                >
                  Try again
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="relative mx-auto mb-6 h-16 w-16">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                  <ClipboardList className="h-8 w-8 text-primary" aria-hidden />
                </div>
                <span className="absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-border">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden />
                </span>
              </div>

              <p className="text-sm font-medium text-primary">Just a moment</p>
              <h1 className="mt-1 text-xl font-semibold text-foreground md:text-2xl">
                Fetching assignment details
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                We&apos;re getting your questions ready. You&apos;ll jump in automatically.
              </p>
              <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-primary" />

              <Card className="mt-8 border-border/60 p-5 text-left shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                  <div className="h-3 w-24 animate-pulse rounded-full bg-muted" />
                  <div className="h-3 w-12 animate-pulse rounded-full bg-muted" />
                </div>
                <div className="cw-fetch-bar mb-5" />
                <div className="space-y-3">
                  <div className="h-3.5 w-2/5 animate-pulse rounded-full bg-muted" />
                  <div className="h-3.5 w-full animate-pulse rounded-full bg-muted" />
                  <div className="h-3.5 w-4/5 animate-pulse rounded-full bg-muted" />
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </LoggedInUserWrapper>
  );
};

export default withAuth(AssignmentHome);
